import { StarsButton, Translation } from '@ps/web-shared-components';
import classNames from 'clsx';
import React from 'react';
import { useSelector } from 'react-redux';

import { select } from '@swf/core/store';
import { FormattedCurrencyAmount } from '@swf/poker';

import LogoSpinner from '../../../../assets/logo-spingo-spinner.svg';
import gameData from '../../../../constants/gameData';
import { GAME_VARIANT_TYPE_MAP } from '../../../../types/Game';
import type { LiveTournament } from '../../../../types/WatchGames';
import { multiplierHelper } from '../../../../utils/numbers';

import {
  TXTCLI_Buy_In,
  TXTCLI_Multiplier,
  TXTCLI_Observe,
  TXTCLI_Something_went_wrongX_please_t_ELL,
  TXTCLI_Top_Prize_1
} from './LiveNow.messages.json';
import styles from './LiveNow.scss';

export interface LiveItemProps {
  liveGame: LiveTournament;
}
const LiveItem: React.FC<LiveItemProps> = ({ liveGame }) => {
  const { tournId, buyIn, prizePool, currency, firstPlacePrize, gameVariant } = liveGame;
  const isEmbedded = useSelector(select.device.isEmbedded);
  const isPlayMoney = useSelector(select.currency.isPlayMoney);
  const gameType = GAME_VARIANT_TYPE_MAP[gameVariant];

  const { icon } = gameData[gameType];
  const multiplier = multiplierHelper({
    buyIn,
    prizePool,
    isPlayMoney
  });

  return (
    <li className={styles.liveItem}>
      <div className={classNames(styles.lobbyBgr, styles[`lobbyBgr${gameType}`])} />
      <main className={styles.liveMain}>
        <div className={styles.logoCover}>
          <div className={styles.logoContainer}>
            {icon}
            <LogoSpinner className={styles.spinner} />
          </div>
        </div>
        <footer className={styles.itemControl}>
          <ul>
            <li className={styles.multiplier}>
              <span className={styles.label}>
                <Translation {...TXTCLI_Multiplier} />
              </span>
              <span className={styles.value}>x{multiplier}</span>
            </li>
            <li>
              <span className={styles.label}>
                <Translation {...TXTCLI_Top_Prize_1} />
              </span>
              <span className={styles.value}>
                <FormattedCurrencyAmount
                  amount={firstPlacePrize}
                  isPlayMoney={isPlayMoney}
                  currency={currency}
                  notationPM="compact"
                  currencyDisplay="narrowSymbol"
                />
              </span>
            </li>
            <li>
              <span className={styles.label}>
                <Translation {...TXTCLI_Buy_In} />
              </span>
              <span className={styles.value}>
                <FormattedCurrencyAmount
                  amount={buyIn}
                  isPlayMoney={isPlayMoney}
                  currency={currency}
                  notationPM="compact"
                  currencyDisplay="narrowSymbol"
                />
              </span>
            </li>
          </ul>
          {isEmbedded && (
            <div className={styles.observeWrapper}>
              <StarsButton
                size="small"
                className={styles.observeButton}
                translation={TXTCLI_Observe}
                variant="text"
                onClick={() =>
                  window.starsweb?.spinandgo?.observe({
                    tournId,
                    isPlayMoney
                  })
                }
              />
            </div>
          )}
        </footer>
      </main>
    </li>
  );
};

interface LiveNowProps {
  games: LiveTournament[];
}

const LiveNow: React.FC<LiveNowProps> = ({ games }) => (
  <div className={styles.tabLive}>
    {games.length ? (
      <ul className={styles.widgetLive}>
        {games.map((game) => (
          <LiveItem liveGame={game} key={game.tournId} />
        ))}
      </ul>
    ) : (
      <div data-testid="empty-container" className={styles.empty}>
        <Translation {...TXTCLI_Something_went_wrongX_please_t_ELL} />
      </div>
    )}
  </div>
);

export default LiveNow;
